import { create } from "zustand";
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  type UserNotification,
} from "@/api/notifications";

interface NotificationsState {
  items: UserNotification[];
  unreadCount: number;
  loading: boolean;
  fetchUnreadCount: () => Promise<void>;
  fetchItems: () => Promise<void>;
  markRead: (id: number) => Promise<void>;
  reset: () => void;
}

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  items: [],
  unreadCount: 0,
  loading: false,

  fetchUnreadCount: async () => {
    try {
      const count = await getUnreadCount();
      set({ unreadCount: count });
    } catch {
      /* polling is best-effort */
    }
  },

  fetchItems: async () => {
    set({ loading: true });
    try {
      const items = await getNotifications();
      set({
        items,
        unreadCount: items.filter((i) => !i.is_read).length,
      });
    } catch {
      /* keep previous items on error */
    } finally {
      set({ loading: false });
    }
  },

  markRead: async (id) => {
    const item = get().items.find((i) => i.id === id);
    if (!item || item.is_read) return;
    // Optimistic: mark locally, then sync with the server
    set((state) => ({
      items: state.items.map((i) =>
        i.id === id ? { ...i, is_read: true } : i,
      ),
      unreadCount: Math.max(0, state.unreadCount - 1),
    }));
    try {
      await markNotificationRead(id);
    } catch {
      /* server will be re-synced on next fetch */
    }
  },

  reset: () => set({ items: [], unreadCount: 0, loading: false }),
}));
